import React, {useId, useState} from "react";
import {Box, Link, Text} from "@chakra-ui/react";
import {useCreditsStore} from "../../store/useCreditsStore.ts";
import SmartIcon from "../SmartIcon.tsx";
import {CREDITS_CONTACT_EMAIL} from "./PricingPackageCard.tsx";

const DISMISSED_STORAGE_KEY_PREFIX = "glix.creditsMigrationBanner.dismissed.";

interface MigrationNoticeProps {
  workspaceId: string;
}

// Keyed by workspace from the parent, so the dismissed state re-reads storage per workspace.
const MigrationNotice: React.FC<MigrationNoticeProps> = ({workspaceId}) => {
  const storageKey = `${DISMISSED_STORAGE_KEY_PREFIX}${workspaceId}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(storageKey) === "1");
  const titleId = useId();

  if (dismissed) return null;

  const handleDismiss = (): void => {
    localStorage.setItem(storageKey, "1");
    setDismissed(true);
  };

  return (
    <Box
      as="section"
      aria-labelledby={titleId}
      className="mt-4 flex w-full items-start gap-3 rounded-xl border border-brandPurple-500/20 bg-brandPurple-500/10 px-5 py-4"
      data-testid="credits-migration-banner"
    >
      <SmartIcon
        className="mt-0.5 shrink-0 fill-brandPurple-500 dark:fill-brandPurple-300"
        name="info-circle"
        size={20}
      />
      <Box className="min-w-0 flex-1">
        <Text id={titleId} className="base2 font-semibold text-n-700 dark:text-n-100">
          Minutes are now credits
        </Text>
        <Text className="mt-1 text-sm leading-6 text-n-500 dark:text-n-300" data-testid="credits-migration-body">
          Glix now bills in credits. Your remaining minutes were converted at 1 minute = 4 credits. Questions? Contact <Link href={`mailto:${CREDITS_CONTACT_EMAIL}`} className="font-semibold text-brandPurple-500 dark:text-brandPurple-300">{CREDITS_CONTACT_EMAIL}</Link>.
        </Text>
      </Box>
      <button
        type="button"
        aria-label="Dismiss"
        className="-mr-2 -mt-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full fill-n-500 transition-colors hover:bg-n-700/5 hover:fill-n-700 dark:fill-n-400 dark:hover:bg-n-100/10 dark:hover:fill-n-100"
        onClick={handleDismiss}
      >
        <SmartIcon className="fill-inherit transition-colors" name="close" size={20}/>
      </button>
    </Box>
  );
};

const CreditsMigrationBanner: React.FC = () => {
  const balance = useCreditsStore((state) => state.balance);
  if (!balance?.migratedFromMinutes) return null;
  return <MigrationNotice key={balance.workspaceId} workspaceId={balance.workspaceId}/>;
};

export default CreditsMigrationBanner;
